import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";

import DashboardLayout from "../../../components/layout/DashboardLayout";
import { useProductViewModel } from "../../../viewmodels/website/useProductViewModel";
import ProductModal from "./components/ProductModal";
import ProductViewModal from "./components/ProductViewModal";
import EditProductModal from "./components/EditProductModal";

const ProductPage = () => {
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    createProduct,
    products,
    loading,
    page,
    setPage,
    search,
    setSearch,
    deleteProduct,
    loadProducts,
  } = useProductViewModel();

  // ============================================
  // INITIALIZE FROM URL
  // ============================================

  useEffect(() => {
    setPage(Number(searchParams.get("page") ?? "1"));

    setSearch(searchParams.get("search") ?? "");
  }, []);

  // ============================================
  // CHANGE PAGE
  // ============================================

  const changePage = (newPage: number) => {
    setPage(newPage);

    setSearchParams({
      page: String(newPage),
      search,
    });
  };

  // ============================================
  // DELETE
  // ============================================

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Delete Product?",
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#64748b",
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    const success = await deleteProduct(id);

    if (!success) return;

    await loadProducts();

    await Swal.fire({
      icon: "success",
      title: "Deleted",
      text: "Product deleted successfully.",
      timer: 1500,
      showConfirmButton: false,
    });
  };

  return (
    <DashboardLayout>
      <div className="p-4 md:p-6">
        {/* ============================================
            HEADER
        ============================================ */}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div>
            <h1 className="text-2xl font-bold">Products</h1>

            <p className="text-gray-500 mt-1">Manage products</p>
          </div>

          <button
            onClick={() => setOpenCreateModal(true)}
            className="bg-primary text-white px-5 py-3 rounded-2xl font-medium hover:opacity-90 transition"
          >
            + Create
          </button>
        </div>

        {/* ============================================
            FILTERS
        ============================================ */}
        <div className="bg-white rounded-xl shadow border border-slate-300 p-4 mb-5">
          <label className="block text-sm font-medium mb-2">Search</label>
          <input
            value={search}
            onChange={(e) => {
              const value = e.target.value;

              setSearch(value);

              setPage(1);

              setSearchParams({
                page: "1",
                search: value,
              });
            }}
            placeholder="Search..."
            className="w-full border border-slate-300 rounded-xl px-4 py-3"
          />
        </div>
        {/* ============================================
            TABLE
        ============================================ */}

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="w-full">
            <table className="w-full min-w-[1300px] text-sm text-left">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Name
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Category
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Sub Category
                  </th>

                  <th className="p-3 border border-slate-200 whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center p-8 border border-slate-200"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : products.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="text-center p-8 border border-slate-200 text-gray-500"
                    >
                      No products found
                    </td>
                  </tr>
                ) : (
                  products.map((x: any) => (
                    <tr key={x.id} className="hover:bg-slate-50 transition">
                      {/* NAME */}
                      <td className="p-3 border border-slate-200 font-medium whitespace-nowrap">
                        {x.name}
                      </td>

                      {/* CATEGORY */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        {x.categoryName ?? "-"}
                      </td>

                      {/* SUB CATEGORY */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        {x.subCategoryName ?? "-"}
                      </td>

                      {/* ACTIONS */}
                      <td className="p-3 border border-slate-200 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {/* VIEW */}
                          <button
                            onClick={() => {
                              setSelectedProductId(x.id);
                              setOpenViewModal(true);
                            }}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            View
                          </button>

                          {/* EDIT */}
                          <button
                            onClick={() => {
                              setSelectedProductId(x.id);
                              setOpenEditModal(true);
                            }}
                            className="bg-yellow-500 hover:bg-yellow-600 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            Edit
                          </button>

                          {/* DELETE */}
                          <button
                            onClick={() => handleDelete(x.id)}
                            className="bg-red-500 hover:bg-red-600 text-white px-3 py-2 rounded-lg text-sm transition"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* ============================================
            PAGINATION
        ============================================ */}

        <div className="mt-4 flex items-center justify-between gap-3">
          <button
            disabled={page === 1}
            onClick={() => changePage(page - 1)}
            className="px-4 py-2 border rounded-xl disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={() => changePage(page + 1)}
            className="px-4 py-2 border rounded-xl"
          >
            Next
          </button>
        </div>
      </div>
      <ProductModal
        isOpen={openCreateModal}
        createProduct={createProduct}
        onClose={() => setOpenCreateModal(false)}
        onSuccess={async () => {
          await loadProducts();
          setOpenCreateModal(false);
        }}
      />
      <ProductViewModal
        isOpen={openViewModal}
        productId={selectedProductId}
        onClose={() => {
          setOpenViewModal(false);
          setSelectedProductId("");
        }}
      />
      <EditProductModal
        isOpen={openEditModal}
        productId={selectedProductId}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedProductId("");
        }}
        onSuccess={async () => {
          await loadProducts();
          setOpenEditModal(false);
          setSelectedProductId("");
        }}
      />
    </DashboardLayout>
  );
};

export default ProductPage;
